import styles from "./bot.module.css";

import {
  ActionIcon,
  Box,
  deepMerge,
  Dialog,
  MantineProvider,
  Stack,
  Text,
} from "@mantine/core";
import { useState } from "react";

import { defaultConfiguration } from "@/libs/config";
import { View, type DefaultConfiguration, type ViewType } from "@/types";
import { useClickOutside } from "@mantine/hooks";

import {
  FeedBackForm,
  LiveChat,
  Navigation,
  ReportAnIssueForm,
  Support,
} from "..";
import { SupportInfoWrapper } from "../faqs/info/wrapper";

export const FeedBackBot = (defaultProps: DefaultConfiguration) => {
  if (!defaultProps.apiKey || !defaultProps.apiURL) {
    throw new Error("API URL and API key not defined");
  }

  const props = deepMerge(defaultConfiguration, defaultProps);
  console.log("Feedback Bot Props:", props);
  const { authStatus, sections, supportGuide } = props;

  const [dialogOpened, setDialogOpened] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>(
    (props?.sections ?? [])[0].id
  );
  const [selectedId, setSelectedId] = useState<null | number>(null);

  const toggleDialog = () => setDialogOpened((prev) => !prev);
  const ref = useClickOutside(() => setDialogOpened(false));

  const goToView = (view: ViewType, id?: number) => {
    setSelectedId(id ?? null);
    setCurrentView(view);
  };

  const viewMap = {
    [View.Chat]: <LiveChat />,
    [View.Feedback]: (
      <FeedBackForm
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        authStatus={authStatus}
        onBack={() => setCurrentView(View.Home)}
        supportGuide={supportGuide}
      />
    ),
    [View.Home]: (
      <Support
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        navigate={(view, id) => {
          if (view === "support-info" && id !== undefined) {
            goToView(View.Info, id);
          } else if (view === "feed-back-form") {
            goToView(View.Feedback);
          }
        }}
        supportGuide={supportGuide}
      />
    ),
    [View.Info]: (
      <SupportInfoWrapper
        id={selectedId}
        onBack={() => setCurrentView(View.Home)}
        supportGuide={supportGuide}
      />
    ),
    [View.Issue]: (
      <ReportAnIssueForm
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        authStatus={authStatus}
        issueType={supportGuide?.system}
      />
    ),
  };

  return (
    <MantineProvider>
      <Dialog
        mb={40}
        onClose={toggleDialog}
        opened={dialogOpened}
        p={0}
        radius="md"
        style={{ maxWidth: 400, width: "100%" }}
        withCloseButton
        ref={ref}
        classNames={{ closeButton: "bg-white rounded-lg mt-2" }}
      >
        <div
          className={styles.dialogContainer}
          style={{
            background: `linear-gradient(to bottom, ${supportGuide?.gradient?.[0]}, transparent 200px)`,
          }}
        >
          <div className={styles.dialogContent}>{viewMap[currentView]}</div>
          <Navigation sections={sections} navigate={goToView} />
        </div>
      </Dialog>

      <ActionIcon
        onClick={toggleDialog}
        radius={4}
        size={40}
        style={{
          backgroundColor: "#e2261c",
          bottom: "20px",
          position: "fixed",
          right: "20px",
          zIndex: 1000,
        }}
        variant="filled"
      >
        <Stack gap={0} mx="auto">
          <Box w="fit-content" mx="auto">
            {props.icon}
          </Box>
          {typeof props.label === "string" ? (
            <Text fz={8} fw={700} ta="center">
              {props.label}
            </Text>
          ) : (
            props.label
          )}
        </Stack>
      </ActionIcon>
    </MantineProvider>
  );
};
